import { useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Compass, Copy, MoreHorizontal, MoreVertical, Menu, PlusSquare, Share, X } from "lucide-react";
import { chromeIntent, copyLink, detectPlatform } from "../../lib/install";
import { useBrand } from "../../context/brand";

const B = ({ children }) => <b className="text-white">{children}</b>;
const ADD = { Icon: PlusSquare, text: <>Toque em <B>Adicionar à Tela de Início</B> (se não aparecer, role a lista ou toque em <B>Mais</B>).</> };
const DONE = { Icon: null, text: <>Toque em <B>Adicionar</B>. Pronto: o app aparece na sua tela inicial.</> };

const GUIDES = {
  "ios-safari": { title: "Instalar no iPhone (Safari)", arrow: "down", arrowText: "O botão Compartilhar fica aqui embaixo",
    steps: [{ Icon: Share, text: <>Toque em <B>Compartilhar</B> (quadrado com seta para cima) na barra do Safari.</> }, ADD, DONE] },
  "ios-chrome": { title: "Instalar no iPhone (Chrome)", arrow: "up", arrowText: "Compartilhar fica na barra de endereço", safari: true,
    steps: [{ Icon: Share, text: <>Toque em <B>Compartilhar</B> (quadrado com seta) no lado direito da barra de endereço, lá em cima. Não achou? Toque em <B>⋯</B> embaixo → <B>Compartilhar</B>.</> }, ADD, DONE] },
  "ios-other": { title: "Instalar no iPhone", arrow: null, safari: true,
    steps: [{ Icon: Menu, text: <>Toque no menu do navegador (<B>☰</B> ou <B>⋯</B>) → <B>Compartilhar</B>.</> }, ADD, DONE] },
  "inapp-ios": { title: "Abra no Safari para instalar", arrow: null, safari: true,
    steps: [{ Icon: MoreHorizontal, text: <>Este navegador do aplicativo não instala apps. Toque em <B>Abrir no Safari</B> abaixo, ou em <B>⋯</B> → <B>Abrir no navegador</B>.</> }, { Icon: Share, text: <>No Safari: <B>Compartilhar</B> → <B>Adicionar à Tela de Início</B>.</> }] },
  "inapp-android": { title: "Abra no Chrome para instalar", arrow: null, chrome: true,
    steps: [{ Icon: MoreVertical, text: <>Este navegador do aplicativo não instala apps. Toque em <B>Abrir no Chrome</B> abaixo, ou em <B>⋮</B> → <B>Abrir no navegador</B>.</> }, { Icon: PlusSquare, text: <>No Chrome: menu <B>⋮</B> → <B>Instalar app</B>.</> }] },
  "android-chrome": { title: "Instalar no Android", arrow: "up", arrowText: "Menu ⋮ do Chrome",
    steps: [{ Icon: MoreVertical, text: <>Toque no menu <B>⋮</B> no canto de cima do navegador.</> }, { Icon: PlusSquare, text: <>Toque em <B>Instalar app</B> ou <B>Adicionar à tela inicial</B>.</> }, DONE] },
  "android-samsung": { title: "Instalar (Samsung Internet)", arrow: null,
    steps: [{ Icon: Menu, text: <>Toque no menu <B>☰</B> embaixo, à direita.</> }, { Icon: PlusSquare, text: <>Toque em <B>Adicionar página a</B> → <B>Tela inicial</B>.</> }, DONE] },
  "android-other": { title: "Instalar no Android", arrow: null, chrome: true,
    steps: [{ Icon: MoreVertical, text: <>Toque no menu do navegador (<B>⋮</B>) → <B>Instalar</B> ou <B>Adicionar à tela inicial</B>.</> }, DONE] },
};

const Arrow = ({ dir, text }) => (
  <motion.div className={`sp-guide-arrow is-${dir}`} aria-hidden="true" data-testid={`install-arrow-${dir}`}
    animate={dir === "down" ? { y: [0, 10, 0] } : { y: [0, -8, 0], x: [0, 6, 0] }} transition={{ duration: 1.2, repeat: Infinity }}>
    {dir === "up" && <ArrowUpRight size={30} strokeWidth={3} />}
    <span>{text}</span>
    {dir === "down" && <ArrowDown size={30} strokeWidth={3} />}
  </motion.div>
);

export const InstallGuide = ({ open, onClose }) => {
  const brand = useBrand();
  const [platform, setPlatform] = useState(detectPlatform);
  const g = GUIDES[platform] || GUIDES["android-other"];
  const top = g.arrow === "down";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div key="guide" className={`sp-sheet-wrap ${top ? "is-top" : ""}`} onClick={onClose} role="presentation"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.15 } }}>
          <motion.div className="sp-sheet" data-testid="install-help" data-platform={platform} onClick={(e) => e.stopPropagation()}
            initial={{ y: top ? "-100%" : "100%" }} animate={{ y: 0 }} exit={{ y: top ? "-100%" : "100%", transition: { duration: 0.15 } }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="sp-brand" data-testid="install-help-title">{g.title}</h2>
                <p className="text-[13px] text-[#9DB0A5] mt-1">{brand.neutral ? "Nosso atendimento" : `Atendimento ${brand.name}`} direto na sua tela inicial.</p>
              </div>
              <button type="button" data-testid="install-help-close" onClick={onClose} className="sp-icon sp-close" aria-label="Fechar"><X size={20} /></button>
            </div>
            <ol className="mt-4 space-y-2.5" data-testid="install-steps">
              {g.steps.map(({ Icon, text }, i) => (
                <li key={i} className={`sp-step-card ${i === 0 ? "is-mine" : ""}`}>
                  <span className="sp-step">{Icon ? <Icon size={15} /> : i + 1}</span>
                  <p className="text-[14.5px] text-[#E8EFE9] leading-snug">{text}</p>
                </li>
              ))}
            </ol>
            {(g.safari || g.chrome) && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                {g.safari && <a data-testid="install-open-safari" href={`x-safari-${window.location.href}`} className="sp-alt-btn"><Compass size={16} /> Abrir no Safari</a>}
                {g.chrome && <a data-testid="install-open-chrome" href={chromeIntent()} className="sp-alt-btn"><Compass size={16} /> Abrir no Chrome</a>}
                <button type="button" data-testid="install-copy-link" onClick={() => copyLink(g.chrome ? "Chrome" : "Safari")} className="sp-alt-btn"><Copy size={16} /> Copiar link</button>
              </div>
            )}
            <button type="button" data-testid="install-help-ok" onClick={onClose} className="sp-cta mt-4">Entendi</button>
            <label className="block mt-3 text-[12.5px] text-[#9DB0A5]">
              Outro navegador?{" "}
              <select data-testid="install-platform-select" value={platform} onChange={(e) => setPlatform(e.target.value)} className="sp-select">
                {Object.entries(GUIDES).map(([k, v]) => <option key={k} value={k}>{v.title}</option>)}
              </select>
            </label>
          </motion.div>
          {g.arrow && <Arrow dir={g.arrow} text={g.arrowText} />}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
